/**
 * ENG-8181: the one place that turns an `.mcp.json` server key into the
 * `mcp__<server>__*` permission patterns that gate tool access.
 *
 * ## The bug this exists to close
 *
 * Three call sites independently rewrote hyphens to underscores when building
 * these patterns — the sub-agent `tools:` renderer, the CLI's `--allowedTools`
 * builder, and the audit meant to catch a mismatch between the two. All three
 * shared the same stated assumption: "Claude Code's allowedTools patterns use
 * underscore-separated names".
 *
 * That assumption is wrong. Tool names carry the server key VERBATIM, hyphens
 * included — a live session exposes `mcp__direct-chat__direct_chat_reply` and
 * `mcp__augmented-admin__debug_get_agent`, not the underscored spellings. So
 * for any server whose key contains a hyphen the rendered pattern matched
 * nothing, and every tool that server exposes was silently filtered out of the
 * sub-agent's registry.
 *
 * Silently is the operative word, and it is why this went unnoticed for so
 * long. A filtered-out server does not error at render time, does not error at
 * spawn time, and produces no log line. It surfaces only as "No such tool
 * available." at the moment a sub-agent tries to use it — which for
 * `channel-message-handler` means the reply it was dispatched to send simply
 * never lands. The audit built to catch exactly this applied the same rewrite,
 * so it compared a wrong expectation against a wrong rendering and reported the
 * fleet clean.
 *
 * Servers affected in the current fleet: `direct-chat`, `augmented-admin`,
 * `composio_gmail-personal-mailbox`, and any hyphenated server added later.
 *
 * ## Why both spellings
 *
 * The raw key is what the runtime actually exposes, so it is the one that must
 * be present. The underscored variant is emitted alongside it rather than
 * dropped: it is what every already-rendered agent on disk carries today, it is
 * harmless (a pattern that matches nothing grants nothing), and keeping it
 * means this change cannot regress a host whose Claude Code build does
 * normalise. Belt and braces on a permission list is cheap; a wrong guess here
 * costs another silent outage.
 */

/** The historical underscored spelling. Kept for the both-forms emit. */
export function sanitizeMcpName(name: string): string {
  return name.replace(/-/g, '_');
}

/**
 * Every wildcard pattern that should appear in an allowlist for one server key.
 *
 * Returns the verbatim form first (the one that actually binds), followed by
 * the underscored form when the key contains a hyphen. Order is stable so
 * rendered files and test snapshots stay diff-clean.
 */
export function mcpWildcardsForServer(serverKey: string): string[] {
  const raw = `mcp__${serverKey}__*`;
  const sanitized = `mcp__${sanitizeMcpName(serverKey)}__*`;
  return sanitized === raw ? [raw] : [raw, sanitized];
}

/**
 * The wildcard patterns for a whole set of server keys, de-duplicated and
 * order-stable.
 */
export function mcpWildcardsForServers(serverKeys: readonly string[]): string[] {
  return Array.from(new Set(serverKeys.flatMap((k) => mcpWildcardsForServer(k))));
}

/**
 * The pattern an allowlist MUST contain for a server's tools to bind.
 *
 * Deliberately the verbatim form only. An audit that accepted the underscored
 * spelling as sufficient would keep reporting a broken agent as healthy, which
 * is the failure ENG-8181 is about.
 */
export function requiredMcpWildcard(serverKey: string): string {
  return `mcp__${serverKey}__*`;
}
